import { Box, Stack, IconButton, Modal as MuiModal } from "@mui/material";
import * as yup from "yup";
import { Close } from "src/assets";
import Text from "../Displays/Text";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import CheckboxInput from "../Inputs/CheckboxInput";
import PrimaryButton from "../Shared/PrimaryButton";
import { yupResolver } from "@hookform/resolvers/yup";

interface SmaCompatibilityCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export interface SmaCompatibilityCheckFormValues {
  checkbox1: boolean;
  checkbox2: boolean;
  checkbox3: boolean;
}

const bodyTextSx = {
  color: "#222",
  fontSize: "16px",
  fontWeight: "400",
  lineHeight: "22px",
};

const sectionTitleSx = {
  color: "#222",
  fontSize: "13px",
  fontWeight: "700",
  lineHeight: "18px",
};

const excludedDevices = [
  "Sunny Boy Smart Energy SB 3600SE-10 oder SB 5000SE-10",
  "Sunny Island SI3.0M, SI4.4M, SI6.0H oder SI8.0H",
];

const SmaCompatibilityCheckModal = ({
  onClose,
  isOpen,
  onSubmit,
}: SmaCompatibilityCheckModalProps) => {
  const smaCompatibilityCheckFormSchema: yup.ObjectSchema<SmaCompatibilityCheckFormValues> =
    yup.object({
      checkbox1: yup
        .boolean()
        .required()
        .test(
          "checkbox1-check",
          "page.contractSummary.error",
          function (value) {
            return value === true || this.parent.checkbox2 === true;
          },
        ),
      checkbox2: yup
        .boolean()
        .required()
        .test(
          "checkbox2-check",
          "page.contractSummary.error",
          function (value) {
            return value === true || this.parent.checkbox1 === true;
          },
        ),
      checkbox3: yup
        .boolean()
        .required()
        .test(
          "checkbox3-check",
          "page.contractSummary.error",
          function (value) {
            return value === true;
          },
        ),
    });

  const form = useForm<SmaCompatibilityCheckFormValues>({
    mode: "onSubmit",
    defaultValues: {
      checkbox1: false,
      checkbox2: false,
      checkbox3: false,
    },
    resolver: yupResolver(smaCompatibilityCheckFormSchema),
  });

  const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);

  useEffect(() => {
    const subscription = form.watch((value) => {
      const hasEnergyManagement =
        value.checkbox1 === true || value.checkbox2 === true;
      const hasNoExcludedDevices = value.checkbox3 === true;

      setFormSubmitDisabled(!(hasEnergyManagement && hasNoExcludedDevices));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <MuiModal
      open={isOpen}
      disableAutoFocus
      onClose={onClose}
      aria-labelledby="title"
      aria-describedby="description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        gap={6}
        sx={{
          m: 4,
          px: 4,
          py: 5,
          bgcolor: "#fff",
          width: "600px",
          minWidth: "285px",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: "14px",
        }}
      >
        <Stack gap={7}>
          <Stack gap={2}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Text
                id="title"
                text={"SMA Energiemanagement"}
                sx={{
                  fontSize: "22px",
                  fontWeight: "295",
                  lineHeight: "26px",
                  fontFamily: "isonType",
                }}
              />
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                  color: theme.palette.grey[500],
                })}
              >
                <Close />
              </IconButton>
            </Stack>

            <Box id="description" sx={bodyTextSx}>
              Eine der folgenden Voraussetzungen muss erfüllt sein:
            </Box>
          </Stack>

          <Stack gap={3}>
            <Stack>
              <CheckboxInput
                control={form.control}
                aria-label="Bei mir ist ein Sunny Home Manager 2.0 installiert."
                label={
                  <Box sx={bodyTextSx}>
                    Bei mir ist ein Sunny Home Manager 2.0 installiert.
                  </Box>
                }
                name="checkbox1"
              />
              <CheckboxInput
                control={form.control}
                aria-label="Ich nutze den SMA Energy Maximizer."
                label={
                  <Box sx={bodyTextSx}>Ich nutze den SMA Energy Maximizer.</Box>
                }
                name="checkbox2"
              />
            </Stack>

            <Stack gap={2}>
              <Box sx={sectionTitleSx}>Weitere SMA Geräte</Box>
              <Box sx={bodyTextSx}>
                StromWallet Dynamic Pro ist mit den allermeisten SMA Geräten
                kompatibel. Ausgenommen sind:
              </Box>
              <Box
                component="ul"
                sx={{
                  ...bodyTextSx,
                  m: 0,
                  pl: 6,
                }}
              >
                {excludedDevices.map((device) => (
                  <Box component="li" key={device}>
                    {device}
                  </Box>
                ))}
              </Box>
              <CheckboxInput
                control={form.control}
                aria-label="Keines dieser Geräte ist in meiner Anlage installiert."
                label={
                  <Box sx={bodyTextSx}>
                    Keines dieser Geräte ist in meiner Anlage installiert.
                  </Box>
                }
                name="checkbox3"
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack gap={4} flexDirection="row" flexWrap="wrap">
          <PrimaryButton
            fullWidth
            sx={{
              textTransform: "none",
            }}
            onClick={onSubmit}
            text={"Weiter"}
            disabled={formSubmitDisabled}
          />
        </Stack>
      </Stack>
    </MuiModal>
  );
};

export default SmaCompatibilityCheckModal;
